import { BulkCreateProviderTerminationsModel, BulkTerminationProjectResponseModel, ImportBulkTerminationFileResponse } from "@app/terminations/import/interfaces";
import { BulkProviderChangeReportResponseModel } from "@app/shared/provider-change-wizards/interfaces";
import { LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { ServiceBase } from "@app/shared";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import UpdateProviderTermination from "@app/provider-terminations/updateProviderTermination";
import { SortDirections } from "@app/shared/waffles";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, NewPatientClaimsEnum } from "@app/projects/servicing-provider";

/** Manages access to CRUD functionality for provider sets. */
export default class ProviderTerminationService extends ServiceBase {
  public async getTerminatedProviders(
    projectId: number,
    projectRunConfigurationId: number,
    regionSetId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    sortedColumn: string,
    sortDirection: SortDirections,
    columnSubKey: string,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[]
  ): Promise<NetworkServicingProviderTerminationModel[]> {
    if (!projectId || !projectRunConfigurationId || !regionSetId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost(
      `providerTerminations/${projectId}/runConfigurations/${projectRunConfigurationId}/regionsets/${regionSetId}`,
      {
        columnSubKey,
        networkAssessmentAnalysisType,
        searchValue,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedVolumeLabels,
        sortDirection,
        sortedColumn,
        specialtyGroupCode
      });
  }

  public async createProviderTerminations(
    customProjectId: number,
    terminateByNPI: boolean,
    terminateByTIN: boolean,
    providerIds: number[],
    reasonName: string,
    reasonDescription: string,
    allSpecialties: boolean,
    allLocations: boolean,
    specialtyGroupCode: string,
  ): Promise<string> {
    return await this.makePost(`providerTerminations/create`, { allLocations, allSpecialties, customProjectId, providerIds, reasonDescription, reasonName, specialtyGroupCode, terminateByNPI, terminateByTIN });
  }

  public async createGhostProviderTerminations(
    customProjectId: number,
    terminateByNPI: boolean,
    reasonName: string,
    reasonDescription: string,
    npis: string[],
  ): Promise<string> {
    return await this.makePost(`providerTerminations/createGhostProviderTerminations`, { customProjectId, npis, reasonDescription, reasonName, terminateByNPI })
  }

  public async getProviderTerminationReasons(): Promise<string[]> {
    return await this.makeGet(`providerTerminations/providerterminationreasons`);
  }

  public async deleteTermination(projectId: number, terminationIds: number[]): Promise<void> {
    return await this.makeDelete(`providerTerminations/${projectId}`, terminationIds);
  }

  public async restoreTermination(projectId: number, terminationIds: number[]): Promise<void> {
    return await this.makePost(`providerTerminations/${projectId}/restoreterminations`, terminationIds);
  }

  public async getProviderTermination(projectId: number, terminationId: number): Promise<NetworkServicingProviderTerminationModel> {
    return await this.makePost<NetworkServicingProviderTerminationModel>(`providerTerminations/${projectId}/getprovidertermination`, terminationId);
  }

  public async getUnprocessedProviderTerminations(projectId: number): Promise<NetworkServicingProviderTerminationModel[]> {
    if (!projectId) {
      return null;
    }

    return await this.makeGet<NetworkServicingProviderTerminationModel[]>(`providerTerminations/${projectId}/getunprocessedproviderterminations`);
  }

  public async updateProviderTermination(projectId: number, updateProviderTermination: UpdateProviderTermination): Promise<void> {
    return await this.makePatch(`providerTerminations/${projectId}`, updateProviderTermination);
  }

  // Bulk Terminations
  public async getBulkTerminationProjects(filterToPciProjects: boolean = false): Promise<BulkTerminationProjectResponseModel[]> {
    const queryString = filterToPciProjects ? `?filterToPciProjects=${filterToPciProjects}` : "";

    // The endpoint takes query params for includeShared, includeHidden, and resourceStatus, but we're good with the defaults
    return await this.makeGet<BulkTerminationProjectResponseModel[]>(`providerTerminations/bulkproviderterminationprojects${queryString}`);
  }

  public async importBulkTerminationFile(bulkNpiTinFile: FormData, terminateByNpi: boolean, terminateByTin: boolean, terminateByAllLocations: boolean, terminateByAllSpecialties: boolean): Promise<ImportBulkTerminationFileResponse> {
    if (!bulkNpiTinFile) {
      return null;
    }

    const queryString = `?terminateByNpi=${terminateByNpi}&terminateByTin=${terminateByTin}${!terminateByAllLocations ? `&allLocations=${terminateByAllLocations}` : ''}${!terminateByAllSpecialties ? `&allSpecialties=${terminateByAllSpecialties}` : ''}`;
    return await this.makePost<ImportBulkTerminationFileResponse>(`providerTerminations/importbulkterminationfile${queryString}`, bulkNpiTinFile, undefined, true, false, false);
  }

  public async bulkCreateProviderTerminations(bulkCreateProviderTerminationsModel: BulkCreateProviderTerminationsModel): Promise<void> {
    return await this.makePost(`providerTerminations/bulkCreateProviderTerminations`, bulkCreateProviderTerminationsModel);
  }

  public async getBulkProviderTerminationExports(): Promise<BulkProviderChangeReportResponseModel[]> {
    return await this.makeGet<BulkProviderChangeReportResponseModel[]>("providerTerminations/bulkproviderterminationexports");
  }
}
